import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useProvider } from '../lib/provider';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import { Gift, Clock, CheckCircle, XCircle, Calendar } from 'lucide-react';

export const KidRewardHistory: React.FC = () => {
    const provider = useProvider();
    const { session } = useAuth();
    const { t } = useTranslation();

    const { data: redemptions, isLoading } = useQuery({
        queryKey: ['redemptions', session.user?.household_id],
        queryFn: () => provider.getRedemptions(session.user!.household_id),
        enabled: !!session.user?.household_id,
    });

    // Filter redemptions for the current kid only
    const kidRedemptions = redemptions?.filter(
        redemption => redemption.kid_user_id === session.user!.id
    );

    if (isLoading) return <div className="p-8 text-center font-bold text-purple-600">{t.common.loading}</div>;

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="text-center space-y-2">
                <h1 className="text-4xl font-black text-slate-900 tracking-tight flex items-center justify-center gap-3">
                    <Clock className="text-purple-600" />
                    {t.kidRewardHistory.title}
                </h1>
            </div>

            {/* Redemptions List */}
            <div className="space-y-4">
                {kidRedemptions?.length === 0 ? (
                    <div className="bg-white rounded-3xl p-12 border-4 border-purple-50 text-center">
                        <Gift className="mx-auto text-purple-100 mb-4" size={64} />
                        <p className="text-slate-400 font-bold text-xl uppercase tracking-wider">{t.kidRewardHistory.noRedemptions}</p>
                    </div>
                ) : (
                    kidRedemptions?.map((redemption) => (
                        <div key={redemption.id} className="bg-white rounded-2xl p-6 border-2 border-purple-50 shadow-sm">
                            <div className="flex items-start justify-between">
                                <div className="flex items-start gap-4">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                        redemption.status === 'approved' 
                                            ? 'bg-green-100 text-green-600'
                                            : redemption.status === 'rejected'
                                                ? 'bg-red-100 text-red-600'
                                                : 'bg-amber-100 text-amber-600'
                                    }`}>
                                        {redemption.status === 'approved' ? (
                                            <CheckCircle size={24} />
                                        ) : redemption.status === 'rejected' ? (
                                            <XCircle size={24} />
                                        ) : (
                                            <Clock size={24} />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-xl font-black text-slate-800 mb-1">{redemption.reward.title}</h3>
                                        <div className="flex items-center gap-2 text-purple-600 font-bold mb-2">
                                            <span>{redemption.reward.cost_points} {t.common.pts}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-slate-500">
                                            <Calendar size={14} />
                                            <span>{t.kidRewardHistory.requestedOn} {new Date(redemption.created_at).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold ${
                                        redemption.status === 'approved' 
                                            ? 'bg-green-100 text-green-700'
                                            : redemption.status === 'rejected'
                                                ? 'bg-red-100 text-red-700'
                                                : 'bg-amber-100 text-amber-700'
                                    }`}>
                                        {redemption.status === 'approved' && <CheckCircle size={14} />}
                                        {redemption.status === 'rejected' && <XCircle size={14} />}
                                        {redemption.status === 'pending' && <Clock size={14} />}
                                        {redemption.status === 'approved' ? t.kidRewardHistory.approved :
                                         redemption.status === 'rejected' ? t.kidRewardHistory.rejected :
                                         t.kidRewardHistory.pending}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
